import React, {useState} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {Box, IconButton, LinearProgress, Paper, Tooltip} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {DataGridPro} from '@mui/x-data-grid-pro';
import CategoryEditDialog from './CategoryEditDialog';
import ErrorPopup from '../../common/ErrorPopup';
import MessagePopup from '../../common/MessagePopup';
import TableToolbar from '../../common/TableToolbar';
import IyoGridToolbar from '../../common/IyoGridToolbar';

/**
 * Grid for one of the two category tables, laid out the same way as the Providers tab:
 * a create button and refresh in the toolbar, a compact DataGridPro, and an edit action
 * per row opening a dialog.
 *
 * Sub-Category passes `parentOptions` (the provider_category rows as {name, value}); that
 * both adds the resolved Category column here and turns on the required parent dropdown in
 * the dialog. Category passes nothing and gets neither.
 */
const CategoryTab = ({entityLabel, queryKey, service, parentOptions}) => {
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [rowEditing, setRowEditing] = useState(null);
  const [errDisplayText, setErrDisplayText] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState('');

  const state = {editMode, editOpen, rowEditing};
  const setState = {setEditMode, setEditOpen, setRowEditing, setErrDisplayText, setErrorOpen,
    setMsgOpen, setMsgText};

  const handleError = (error) => {
    setErrorOpen(false);
    setErrDisplayText(`${error.message}` +
      `${error.response?.data ? ': ' + JSON.stringify(error.response.data) : ''}`);
    setErrorOpen(true);
  };

  const rows = useQuery(queryKey, async () => service.getAll(), {
    onError: handleError,
    retry: false,
    staleTime: Infinity
  });

  const handleCreate = () => {
    setEditMode('create');
    setRowEditing(null);
    setEditOpen(true);
  };

  const handleEdit = (row) => {
    setEditMode('edit');
    setRowEditing(row);
    setEditOpen(true);
  };

  const parentName = (id) => parentOptions?.find((o) => o.value === id)?.name ?? '';

  const columns = [
    {field: 'id', headerName: 'ID', description: 'ID', width: 70},
    {field: 'name', headerName: 'Name', description: 'Name', minWidth: 220},
    ...(parentOptions ? [{
      field: 'category_id',
      headerName: 'Category',
      description: 'Parent provider category',
      minWidth: 180,
      valueGetter: (params) => parentName(params.row.category_id)
    }] : []),
    {field: 'description', headerName: 'Description', description: 'Description', minWidth: 340},
    {
      field: 'active',
      headerName: 'Active',
      description: 'Active',
      width: 100,
      // params.value is post-valueGetter, i.e. the string below - not the raw 0/1.
      cellClassName: (params) => params.value === 'true' ? 'bool-cell-true' : 'bool-cell-false',
      valueGetter: (params) => params.row.active === 1 ? 'true' : 'false'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      description: 'Actions',
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title='Edit'>
          <IconButton color='primary' onClick={() => handleEdit(params.row)}>
            <EditIcon/>
          </IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <Box>
      <TableToolbar
        editHandler={handleCreate}
        onRefresh={async () => queryClient.invalidateQueries(queryKey)}
      />
      <Paper sx={{height: '70vh'}}>
        <DataGridPro
          columns={columns}
          rows={rows.data || []}
          slots={{loadingOverlay: LinearProgress, toolbar: IyoGridToolbar}}
          loading={rows.isLoading}
          density='compact'
          sx={{
            '& .bool-cell-true': {color: 'darkgreen'},
            '& .bool-cell-false': {color: 'darkred'}
          }}
        />
      </Paper>
      <CategoryEditDialog
        entityLabel={entityLabel}
        service={service}
        queryKey={queryKey}
        parentOptions={parentOptions}
        state={state}
        setState={setState}
      />
      <MessagePopup open={msgOpen} setOpen={setMsgOpen} displayText={msgText}/>
      <ErrorPopup open={errorOpen} setOpen={setErrorOpen} displayText={errDisplayText}/>
    </Box>
  );
};

export default CategoryTab;
